import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, RotateCcw } from "lucide-react";
import {
  fetchShadowZoneCatalog,
  fetchShadowZones,
  saveShadowZones,
} from "../lib/api";

/**
 * ShadowZoneCustomiser — tap-the-areas-where-your-shadow-lives picker.
 *
 * Renders a face outline SVG with one circular tap-target per
 * anatomical zone (mustache / cheeks / jowls / jaw / chin / under
 * chin). Tapped zones light up; the AR mirror's color-corrector
 * polygon then renders only at those zones (anchored to MediaPipe
 * landmarks so it tracks pixel-precisely on the user's actual face).
 *
 * Modes:
 *   - "settings"   — full card, fetches + saves the user's current zones
 *   - "onboarding" — same UI; parent wires onSaved with the selected ids
 *                    and we PUT to the backend before navigation
 */
export default function ShadowZoneCustomiser({
  mode = "settings",
  onSaved,
  onSkip,
  authed = true,
  initialSelected = null,
}) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(
    () => new Set(initialSelected || []),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cat = await fetchShadowZoneCatalog();
        if (cancelled) return;
        setCatalog(cat.zones);
        if (authed && !initialSelected) {
          try {
            const profile = await fetchShadowZones();
            if (cancelled) return;
            setSelected(new Set(profile.zones || []));
          } catch {
            /* not authed yet — start empty */
          }
        }
      } catch {
        toast.error("Couldn't load the zone map. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authed, initialSelected]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => setSelected(new Set());

  const handleSave = async () => {
    if (!authed) {
      onSaved?.(Array.from(selected));
      return;
    }
    setSaving(true);
    try {
      const data = await saveShadowZones(Array.from(selected));
      toast.success(
        data.zones.length === 0
          ? "Cleared — back to the default lower-face zone."
          : `Saved ${data.zones.length} shadow zone${
              data.zones.length === 1 ? "" : "s"
            }.`,
      );
      onSaved?.(data.zones);
    } catch {
      toast.error("Couldn't save. Try again in a sec.");
    } finally {
      setSaving(false);
    }
  };

  // Pre-sorted by SVG y-position so the legend reads top → bottom.
  const sortedCatalog = useMemo(
    () =>
      catalog
        ? [...catalog].sort(
            (a, b) => a.svg_cy - b.svg_cy || a.svg_cx - b.svg_cx,
          )
        : null,
    [catalog],
  );

  if (loading) {
    return (
      <div className="text-sm text-[#9B6A6C] flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading the zone map…
      </div>
    );
  }
  if (!catalog || catalog.length === 0) {
    return <div className="text-sm text-[#9B6A6C]">Couldn't load the map.</div>;
  }

  return (
    <div data-testid="shadow-zone-customiser">
      <p className="text-sm text-[#5C4B51] mb-4 leading-snug">
        Tap any area where shadow shows up for you. Trouvé in the AR mirror
        will guide your corrector to those zones — and only those.
        Leave it blank to use our default full-lower-face zone.
      </p>

      <div className="flex flex-col items-center gap-4">
        <div
          className="relative bg-[#FDF6F3] border border-[#F2E8E9] rounded-2xl p-5 w-full max-w-[360px]"
          data-testid="shadow-zone-canvas"
        >
          <svg
            viewBox="0 0 200 240"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
            role="img"
            aria-label="Tappable face map for marking shadow zones"
          >
            {/* Hair / scalp silhouette so the face reads as anatomical */}
            <path
              d="M52,80 Q50,38 100,30 Q150,38 148,80 Q156,90 152,100 L48,100 Q44,90 52,80 Z"
              fill="#EADDD7"
              opacity={0.55}
            />
            {/* Face oval */}
            <ellipse
              cx="100" cy="120" rx="56" ry="78"
              fill="#FBF1EE" stroke="#EADDD7" strokeWidth="1.4"
            />
            {/* Eyes (decorative reference points) */}
            <ellipse cx="80" cy="110" rx="3" ry="1.3" fill="#9B6A6C" opacity="0.5" />
            <ellipse cx="120" cy="110" rx="3" ry="1.3" fill="#9B6A6C" opacity="0.5" />
            {/* Brows */}
            <path d="M72,102 Q80,99 88,103" stroke="#9B6A6C" strokeWidth="1.4" fill="none" opacity="0.5" />
            <path d="M112,103 Q120,99 128,102" stroke="#9B6A6C" strokeWidth="1.4" fill="none" opacity="0.5" />
            {/* Nose */}
            <path
              d="M100,120 Q97,135 94,140 Q98,143 102,143 Q104,141 106,140 Q103,135 100,120"
              stroke="#9B6A6C" strokeWidth="1" fill="none" opacity="0.45"
            />
            {/* Lips */}
            <path
              d="M88,162 Q95,159 100,160 Q105,159 112,162 Q105,166 100,166 Q95,166 88,162 Z"
              fill="#D4A8A4" opacity="0.6"
            />

            {/* Tap targets — one circle per zone */}
            {catalog.map((z) => {
              const on = selected.has(z.id);
              return (
                <g key={z.id} data-testid={`shadow-zone-target-${z.id}`}>
                  {/* Larger, transparent click target so it's easy to tap on mobile.
                      We add +2 (not +6) so adjacent zones don't overlap their hit
                      areas — see catalog: mustache (cy=145) and upper_lip (cy=160)
                      are only 15px apart in SVG space. The legend below provides a
                      100%-reliable per-zone toggle when SVG taps are ambiguous. */}
                  <circle
                    cx={z.svg_cx} cy={z.svg_cy} r={z.svg_r + 2}
                    fill="transparent" cursor="pointer"
                    onClick={() => toggle(z.id)}
                    style={{ touchAction: "manipulation" }}
                  >
                    <title>{z.label} — {z.description}</title>
                  </circle>
                  {/* Visible halo (the actual zone visual) */}
                  <circle
                    cx={z.svg_cx} cy={z.svg_cy} r={z.svg_r}
                    fill={on ? "#D4A8A4" : "transparent"}
                    fillOpacity={on ? 0.55 : 0}
                    stroke={on ? "#9B6A6C" : "#9B6A6C"}
                    strokeWidth={on ? 1.6 : 1.2}
                    strokeDasharray={on ? "0" : "2,3"}
                    pointerEvents="none"
                    style={{ transition: "fill-opacity 180ms ease, stroke-width 180ms ease" }}
                  />
                  {on && (
                    <circle
                      cx={z.svg_cx} cy={z.svg_cy} r={z.svg_r * 0.45}
                      fill="white" pointerEvents="none">
                      <animate attributeName="r"
                        values={`${z.svg_r * 0.45};${z.svg_r * 0.55};${z.svg_r * 0.45}`}
                        dur="2.2s" repeatCount="indefinite" />
                    </circle>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        <div className="w-full flex items-center justify-between flex-wrap gap-3">
          <div
            className="text-xs text-[#8A7D82]"
            data-testid="shadow-zone-count"
          >
            {selected.size === 0
              ? "No zones selected — we'll use our default."
              : `${selected.size} zone${selected.size === 1 ? "" : "s"} selected`}
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1.5 text-xs text-[#8A7D82] hover:text-[#2C2123] underline-offset-4 hover:underline transition-colors"
                data-testid="shadow-zone-reset"
              >
                <RotateCcw className="h-3 w-3" />
                Clear all
              </button>
            )}
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="text-sm text-[#8A7D82] hover:text-[#2C2123] underline-offset-4 hover:underline transition-colors"
                data-testid="shadow-zone-skip"
              >
                Skip for now
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-wait"
              data-testid="shadow-zone-save"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "onboarding" ? "Continue" : "Save"}
            </button>
          </div>
        </div>

        {/* Compact legend for clarity — labels every zone we render */}
        {sortedCatalog && (
          <div
            className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#8A7D82] w-full mt-2"
            data-testid="shadow-zone-legend"
          >
            {sortedCatalog.map((z) => {
              const on = selected.has(z.id);
              return (
                <button
                  key={`legend-${z.id}`}
                  type="button"
                  onClick={() => toggle(z.id)}
                  className={`flex items-center gap-2 text-left py-1 transition-colors ${
                    on ? "text-[#9B6A6C] font-medium" : "hover:text-[#2C2123]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block h-2.5 w-2.5 rounded-full border ${
                      on
                        ? "bg-[#D4A8A4] border-[#9B6A6C]"
                        : "border-[#9B6A6C]/50 bg-transparent"
                    }`}
                  />
                  <span className="truncate">{z.label}</span>
                  {on && <Check className="h-3 w-3 text-[#9B6A6C] shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
